import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type Slider from 'react-slick';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { usePrefersReducedMotion } from '@swf/core/hooks/usePrefersReducedMotion';
import type { WidgetFC } from '@swf/core/types/Widget';
import { Carousel } from '@swf/sports/components';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';

import styles from './SportsCmsBannerWidget.scss';
import DynamicBanner from './components/DynamicBanner/DynamicBanner';
import LoadingState from './components/LoadingState/LoadingState';
import StaticBanner from './components/StaticBanner/StaticBanner';
import { sportsCmsBannerWidgetModel, useSportsCmsBannerWidget } from './model';
import type {
  DynamicBanner as DynamicBannerType,
  ImageFetchPriority,
  StaticBanner as StaticBannerType
} from './model/types';
import { isStaticBanner } from './utils/isStaticBanner';

const getImagePriority = (index: number): ImageFetchPriority => (index === 0 ? 'high' : 'low');

const SportsCmsBannerWidget: WidgetFC = () => {
  const sliderRef = useRef<Slider>(null);
  const { widgetSelect, widgetConfig, widgetDispatch } = useSportsCmsBannerWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const banners = useSelector(widgetSelect.banners);
  const error = useSelector(widgetSelect.error);
  const marketIds = useSelector(widgetSelect.getMarketIds);

  const prefersReducedMotion = usePrefersReducedMotion();

  useMarketUpdates(marketIds, widgetDispatch.marketUpdates);

  useEffect(() => {
    if (!prefersReducedMotion) sliderRef.current?.slickPlay();
  }, [prefersReducedMotion]);

  const noBannersReturned = Boolean(error || banners?.length === 0);

  if (!isLoading && noBannersReturned) {
    return null;
  }

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.boxShadow]: widgetConfig.boxShadow
      })}
    >
      {isLoading && <LoadingState />}

      {!isLoading && banners && (
        <Carousel sliderRef={sliderRef} autoplay={!prefersReducedMotion}>
          {banners.map((banner: StaticBannerType | DynamicBannerType, index) => {
            const priority = getImagePriority(index);

            if (isStaticBanner(banner)) {
              return <StaticBanner key={banner.id} {...banner} priority={priority} />;
            }

            return <DynamicBanner key={banner.id} {...banner} priority={priority} />;
          })}
        </Carousel>
      )}
    </div>
  );
};

SportsCmsBannerWidget.model = sportsCmsBannerWidgetModel;

export default withWidgetTestId(SportsCmsBannerWidget, 'sports-cms-banner');
